import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { triggerBlobDownload, blobErrorMessage } from "src/utils/download_blob";

export const orderManagementSlide = createSlice({
    name: "orderManagement",
    initialState: {
        page: 0,
        pageCount: 0,
        listData: [],

        // Export to Excel
        exportLoading: false,
        exportError: null,

        success: false,
        fail: false,

        detailData: null,
        responseData: null,
        errorDetailData: null,

        // Update status
        updateStatusSuccess: false,
        updateStatusFail: false,
        updateStatusResponseData: null,

        // Update shipping
        updateShippingSuccess: false,
        updateShippingFail: false,
        updateShippingResponseData: null,

        // Cancel order
        cancelOrderLoading: false,
        cancelOrderSuccess: false,
        cancelOrderFail: false,
        cancelOrderResponseData: null,

        // Statistics
        statisticsData: null,
    },
    reducers: {
        doExportOrderManagementRequest: (state) => {
            state.exportLoading = true;
            state.exportError = null;
        },
        doExportOrderManagementSuccess: (state) => {
            state.exportLoading = false;
        },
        doExportOrderManagementFail: (state, action) => {
            state.exportLoading = false;
            state.exportError = action.payload || "Export failed. Please try again.";
        },

        doGetOrderManagementListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload?.current_page || 1;
            state.pageCount = action.payload?.total_pages || 0;
        },
        doGetOrderManagementListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
        },

        doGetOrderManagementDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetOrderManagementDetailFail: (state, action) => {
            state.detailData = null;
        },

        doUpdateOrderStatusSuccess: (state, action) => {
            state.updateStatusSuccess = true;
            state.updateStatusFail = false;
            state.updateStatusResponseData = action.payload;
        },
        doUpdateOrderStatusFail: (state, action) => {
            state.updateStatusSuccess = false;
            state.updateStatusFail = true;
            state.errorDetailData = action.payload;
        },

        doUpdateShippingInfoSuccess: (state, action) => {
            state.updateShippingSuccess = true;
            state.updateShippingFail = false;
            state.updateShippingResponseData = action.payload;
        },
        doUpdateShippingInfoFail: (state, action) => {
            state.updateShippingSuccess = false;
            state.updateShippingFail = true;
            state.errorDetailData = action.payload;
        },

        doGetOrderStatisticsSuccess: (state, action) => {
            state.statisticsData = action.payload.data;
        },
        doGetOrderStatisticsFail: (state, action) => {
            state.statisticsData = null;
        },

        doCancelOrderRequest: (state) => {
            state.cancelOrderLoading = true;
            state.cancelOrderSuccess = false;
            state.cancelOrderFail = false;
        },
        doCancelOrderSuccess: (state, action) => {
            state.cancelOrderLoading = false;
            state.cancelOrderSuccess = true;
            state.cancelOrderFail = false;
            state.cancelOrderResponseData = action.payload;
        },
        doCancelOrderFail: (state, action) => {
            state.cancelOrderLoading = false;
            state.cancelOrderSuccess = false;
            state.cancelOrderFail = true;
            state.errorDetailData = action.payload;
        },

        resetOrderManagementStateSuccess: (state, action) => {
            state.success = false;
            state.fail = false;
            state.errorDetailData = null;
            state.updateStatusSuccess = false;
            state.updateStatusFail = false;
            state.updateStatusResponseData = null;
            state.updateShippingSuccess = false;
            state.updateShippingFail = false;
            state.updateShippingResponseData = null;
            state.cancelOrderSuccess = false;
            state.cancelOrderFail = false;
            state.cancelOrderResponseData = null;
        },

        resetOrderManagementSuccess: (state, action) => {
            state.detailData = null;
        },
    },
});

export const {
    doGetOrderManagementListSuccess,
    doGetOrderManagementListFail,

    doGetOrderManagementDetailSuccess,
    doGetOrderManagementDetailFail,

    doUpdateOrderStatusSuccess,
    doUpdateOrderStatusFail,
    doUpdateShippingInfoSuccess,
    doUpdateShippingInfoFail,

    doGetOrderStatisticsSuccess,
    doGetOrderStatisticsFail,

    doCancelOrderRequest,
    doCancelOrderSuccess,
    doCancelOrderFail,
    
    resetOrderManagementStateSuccess,
    resetOrderManagementSuccess,
    doExportOrderManagementRequest,
    doExportOrderManagementSuccess,
    doExportOrderManagementFail,
} = orderManagementSlide.actions;

// Thunks
export const doGetOrderManagementList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/orders?page=${param.page}&size=${param.limit || 20}`;

        if (param.status) {
            url += `&status=${encodeURIComponent(param.status)}`;
        }
        if (param.search) {
            url += `&search=${encodeURIComponent(param.search)}`;
        }
        if (param.start_date) url += `&start_date=${param.start_date}`;
        if (param.end_date) url += `&end_date=${param.end_date}`;

        const response = await axios.get(url);
        dispatch(doGetOrderManagementListSuccess(response.data));
    } catch (error) {
        dispatch(doGetOrderManagementListFail(error.response));
    }
};

export const doGetOrderManagementDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/orders/${param.order_id}`;
        const response = await axios.get(url);
        dispatch(doGetOrderManagementDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetOrderManagementDetailFail(error.response));
    }
};

export const doUpdateOrderStatus = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/orders/${param.order_id}/status`;
        const response = await axios.put(url, {
            order_status: param.order_status
        });
        dispatch(doUpdateOrderStatusSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateOrderStatusFail(error.response));
    }
};

export const doUpdateShippingInfo = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/orders/${param.order_id}/shipping`;
        const response = await axios.put(url, {
            tracking_number: param.tracking_number,
            shipping_carrier: param.shipping_carrier,
            order_status: param.order_status || 'shipped'
        });
        dispatch(doUpdateShippingInfoSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateShippingInfoFail(error.response));
    }
};

export const doGetOrderStatistics = () => async (dispatch) => {
    try {
        const url = `/api/admin/orders/statistics`;
        const response = await axios.get(url);
        dispatch(doGetOrderStatisticsSuccess(response.data));
    } catch (error) {
        dispatch(doGetOrderStatisticsFail(error.response));
    }
};

export const doCancelOrder = (param) => async (dispatch) => {
    dispatch(doCancelOrderRequest());

    try {
        const url = `/api/admin/orders/${param.order_id}/cancel`;
        const response = await axios.post(url, {
            cancel_reason: param.cancel_reason
        });
        dispatch(doCancelOrderSuccess(response.data));
        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to cancel order";
        dispatch(doCancelOrderFail(errorMessage));
        throw new Error(errorMessage);
    }
};

// Selectors
export const getOrderManagementList = (state) => state.orderManagement?.listData || [];
export const getOrderManagementListPage = (state) => state.orderManagement?.page || 1;
export const getOrderManagementListPageCount = (state) => state.orderManagement?.pageCount || 0;
export const getOrderManagementDetail = (state) => state.orderManagement?.detailData || null;
export const getOrderManagementSuccess = (state) => state.orderManagement?.success || false;
export const getOrderManagementFail = (state) => state.orderManagement?.fail || false;
export const getOrderManagementErrorDetail = (state) => state.orderManagement?.errorDetailData || null;

export const getUpdateOrderStatusSuccess = (state) => state.orderManagement?.updateStatusSuccess || false;
export const getUpdateOrderStatusFail = (state) => state.orderManagement?.updateStatusFail || false;

export const getUpdateShippingSuccess = (state) => state.orderManagement?.updateShippingSuccess || false;
export const getUpdateShippingFail = (state) => state.orderManagement?.updateShippingFail || false;

export const getCancelOrderLoading = (state) => state.orderManagement?.cancelOrderLoading || false;
export const getCancelOrderSuccess = (state) => state.orderManagement?.cancelOrderSuccess || false;
export const getCancelOrderFail = (state) => state.orderManagement?.cancelOrderFail || false;
export const getCancelOrderResponse = (state) => state.orderManagement?.cancelOrderResponseData || null;

export const getOrderStatistics = (state) => state.orderManagement?.statisticsData || null;

// Export reducer by default
export default orderManagementSlide.reducer;

// Export the current filter set (ALL pages, not just the visible one) as an .xlsx.
export const doExportOrderManagement = (param = {}) => async (dispatch) => {
    dispatch(doExportOrderManagementRequest());
    try {
        const params = {};
        if (param.status) params.status = param.status;
        if (param.search) params.search = param.search;
        if (param.start_date) params.start_date = param.start_date;
        if (param.end_date) params.end_date = param.end_date;
        const response = await axios.get("/api/admin/orders/export", { params, responseType: "blob" });
        triggerBlobDownload(response, "Product_Orders");
        dispatch(doExportOrderManagementSuccess());
    } catch (error) {
        dispatch(doExportOrderManagementFail(await blobErrorMessage(error)));
    }
};

export const getExportOrderManagementLoading = (state) => state.orderManagement?.exportLoading || false;
export const getExportOrderManagementError = (state) => state.orderManagement?.exportError || null;
